import React from 'react';
import { Message } from '@chatscope/chat-ui-kit-react';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { FileText } from '@lifeomic/chromicons';
import {
  AttachmentType,
  PostDetails_ActivePost_Fragment,
} from '../__generated__/api.graphql';
import { useIntl, defineMessages, IntlFormatters } from 'react-intl';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import {
  FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
  findCoding,
} from '@lifeomic/fhir-constants';

const messages = defineMessages({
  fileServiceAttachment: {
    id: 'agentChat.chatAttachmentMessage.fileServiceAttachment',
    defaultMessage: 'PDF Document',
  },
});

export type ChatAttachmentNode =
  PostDetails_ActivePost_Fragment['attachmentsV2']['edges'][number]['node'];

export type ChatAttachmentMessageProps = {
  post: PostDetails_ActivePost_Fragment;
  attachment: ChatAttachmentNode;
  currentUserId: string;
  patient: fhir.Patient;
  onOpenImage: (url: string) => void;
  onOpenPdf: (url: string) => void;
};

const getAttachmentUrl = (attachment: ChatAttachmentNode) =>
  attachment.originalUrl ?? attachment.url ?? null;

const getAttachmentThumbnail = (attachment: ChatAttachmentNode) =>
  attachment.thumbnails[0] ?? attachment.originalUrl ?? attachment.url ?? null;

const useStyles = makeStyles(() => ({
  imageThumbnail: {
    maxWidth: 220,
    display: 'block',
  },
  pdfButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pdfFileNameText: {
    textDecoration: 'underline',
  },
}));

const getDisplayFileName = ({
  attachment,
  formatMessage,
}: {
  attachment: ChatAttachmentNode;
  formatMessage: IntlFormatters['formatMessage'];
}) => {
  if (attachment.subType === 'file-service') {
    return formatMessage(messages.fileServiceAttachment);
  }

  return attachment.fileName ?? formatMessage(messages.fileServiceAttachment);
};

export const ChatAttachmentMessage: React.FC<ChatAttachmentMessageProps> = ({
  post,
  attachment,
  currentUserId,
  patient,
  onOpenImage,
  onOpenPdf,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const authorUserId = post.author?.userId ?? '';
  const direction = authorUserId === currentUserId ? 'outgoing' : 'incoming';

  const handler =
    attachment.type === AttachmentType.PDF ? onOpenPdf : onOpenImage;

  const mutation = apiHooks.useAPIMutation(
    'GET /v1/fhir/files/projects/:projectId/files/:fileId',
    {
      onSuccess: data => {
        if (!data.downloadUrl) {
          return;
        }

        handler(data.downloadUrl);
      },
    },
  );

  const url = getAttachmentUrl(attachment);
  if (!url) {
    return null;
  }

  const handleOpenAttachment = () => {
    if (!mutation.isLoading && attachment.subType === 'file-service') {
      const { code: projectId } = findCoding(
        patient?.meta?.tag as fhir3.Coding[],
        {
          system: FHIR_DATASET_TAG_SYSTEM_LIFEOMIC,
          requireCode: true,
        },
      );

      mutation.mutate({
        projectId,
        subject: patient.id!,
        fileId: attachment.externalId,
        include: 'downloadUrl',
        includeContentDisposition: false,
      });
      return;
    }

    handler(url);
  };

  if (attachment.type === AttachmentType.IMAGE) {
    const thumbnail = getAttachmentThumbnail(attachment);
    if (!thumbnail) {
      return null;
    }

    return (
      <Message
        model={{
          message: '',
          sentTime: post.createdAt,
          sender: post.author?.profile.displayName ?? '',
          direction,
          position: 'single',
        }}
      >
        <Message.CustomContent>
          <ButtonUnstyled onClick={handleOpenAttachment} title="View image">
            <img alt="" src={thumbnail} className={classes.imageThumbnail} />
          </ButtonUnstyled>
        </Message.CustomContent>
      </Message>
    );
  }

  if (attachment.type === AttachmentType.PDF) {
    const fileName = getDisplayFileName({ attachment, formatMessage });

    return (
      <Message
        model={{
          message: '',
          sentTime: post.createdAt,
          sender: post.author?.profile.displayName ?? '',
          direction,
          position: 'single',
        }}
      >
        <Message.CustomContent>
          <ButtonUnstyled
            onClick={handleOpenAttachment}
            title={`Open ${fileName}`}
            className={classes.pdfButton}
          >
            <FileText />
            <Text
              color={direction === 'outgoing' ? 'inverse' : 'default'}
              className={classes.pdfFileNameText}
            >
              {fileName}
            </Text>
          </ButtonUnstyled>
        </Message.CustomContent>
      </Message>
    );
  }

  return null;
};
